import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import {
    Table,
    Typography,
    Row,
    Col,
    Select,
    Button,
    Tag,
    Input,
    Badge,
} from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    SyncOutlined,
    UserAddOutlined,
    ReloadOutlined,
    LoadingOutlined,
    FileExcelOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProbationReviewModal from "../../components/employee/ProbationReviewModal";
import "../../components/TeamLeaves.css";

const { Title } = Typography;
const { Option } = Select;

const STATUS_TABS = [
    { key: "Pending Approvals", label: "Pending Approvals", color: "#faad14" },
    { key: "Confirmed", label: "Confirmed", color: "#4CAF50" },
    { key: "Extended", label: "Extended", color: "#0e497b" },
    { key: "Separated", label: "Separated", color: "#f5222d" },
    { key: "Probation List", label: "Probation List", color: "#722ed1" },
];

const STATUS_TAG_COLOR = {
    Pending: "orange",
    Confirm: "green",
    Extend: "blue",
    Separated: "red",
};

export default function ProbationReviewHr() {
    const { backUrl, authData } = useContext(AuthContext);

    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [activeTab, setActiveTab] = useState("Pending Approvals");

    const [tabCounts, setTabCounts] = useState({
        "Pending Approvals": 0,
        Confirmed: 0,
        Extended: 0,
        Separated: 0,
        "Probation List": 0,
    });

    const [searchName, setSearchName] = useState("");
    const [searchEmpCode, setSearchEmpCode] = useState("");

    const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });

    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [organisationFilter, setOrganisationFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [designationFilter, setDesignationFilter] = useState("");

    const headers = { Authorization: `Bearer ${authData?.access}` };

    const fetchOrganisation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, { headers });
            setOrganisations(res.data.results || []);
        } catch (_) { }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers }
            );
            setDepartments(res.data.results || []);
        } catch (_) { }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers }
            );
            setDesignations(res.data.results || []);
        } catch (_) { }
    };

    const fetchData = async (tab) => {
        const tabKey = tab ?? activeTab;
        setLoading(true);
        try {
            let url;
            if (tabKey === "Probation List") {
                url = `${backUrl}/api/probation/probation-reviews/probation_employees/`;
            } else {
                const statusMap = {
                    "Pending Approvals": "Pending",
                    Confirmed: "Confirm",
                    Extended: "Extend",
                    Separated: "Separated",
                };
                // HR view: show all records by overall_status globally
                // is_ready=true for Pending Approvals tab: only show records where it's currently HR's turn
                url = `${backUrl}/api/probation/probation-reviews/?view_mode=hr&overall_status=${statusMap[tabKey]}${tabKey === "Pending Approvals" ? "&is_ready=true" : ""}`;
            }

            const res = await axios.get(url, { headers });
            const data = res.data.results || [];
            setEmployees(data);

            // Keep the "Probation List" badge in sync with what the table
            // actually shows: the backend's counts/ endpoint doesn't know
            // about the Confirm/Extend/Separated exclusion applied
            // client-side below, so recompute it here from the same rule
            // whenever we load this tab.
            if (tabKey === "Probation List") {
                const validCount = data.filter(
                    (i) => !["Confirm", "Extend", "Separated"].includes(i.overall_status)
                ).length;
                setTabCounts((prev) => ({ ...prev, "Probation List": validCount }));
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch probation data.");
        } finally {
            setLoading(false);
        }
    };

    const fetchCounts = async () => {
        try {
            const res = await axios.get(
                `${backUrl}/api/probation/probation-reviews/counts/?view_mode=hr`,
                { headers }
            );
            const d = res.data;
            setTabCounts((prev) => ({
                ...prev,
                // HR Pending = all pending reviews globally (not just "ready for HR")
                "Pending Approvals": d.Ready ?? 0,   // Only show records ready for HR action
                Confirmed: d.Confirmed ?? 0,
                Extended: d.Extended ?? 0,
                Separated: d.Separated ?? 0,
                // NOTE: Probation List count is intentionally left as-is here.
                // The backend's raw ProbationList count doesn't apply the
                // Confirm/Extend/Separated exclusion used in the UI, so it
                // gets corrected in fetchData() once that tab's data loads.
                "Probation List": prev["Probation List"] || d.ProbationList || 0,
            }));
        } catch (_) { }
    };

    useEffect(() => {
        fetchOrganisation();
        fetchData("Pending Approvals");
        fetchCounts();
    }, []);

    useEffect(() => {
        fetchData(activeTab);
    }, [activeTab]);

    // Client-side filtering
    useEffect(() => {
        let filtered = [...employees];
        const getEmp = (item) => item.employee;

        // Guard: Probation List should never show already-confirmed reviews,
        // even if the backend endpoint mistakenly includes them.
        if (activeTab === "Probation List") {
            filtered = filtered.filter(
                (i) => !["Confirm", "Extend", "Separated"].includes(i.overall_status)
            );
        }

        if (organisationFilter)
            filtered = filtered.filter(
                (i) => getEmp(i)?.organisation_name === organisationFilter
            );
        if (departmentFilter)
            filtered = filtered.filter(
                (i) => getEmp(i)?.department_name === departmentFilter
            );
        if (designationFilter)
            filtered = filtered.filter(
                (i) => getEmp(i)?.designation_name === designationFilter
            );
        if (searchEmpCode)
            filtered = filtered.filter((i) =>
                getEmp(i)?.emp_code
                    ?.toLowerCase()
                    .includes(searchEmpCode.toLowerCase())
            );
        if (searchName)
            filtered = filtered.filter((i) => {
                const e = getEmp(i);
                return `${e?.first_name || ""} ${e?.last_name || ""}`
                    .toLowerCase()
                    .includes(searchName.toLowerCase());
            });

        setPagination((prev) => ({ ...prev, current: 1 }));
        setFilteredEmployees(filtered);
    }, [
        organisationFilter,
        departmentFilter,
        designationFilter,
        searchName,
        searchEmpCode,
        activeTab,
        employees,
    ]);

    // Probation List tab now returns ProbationReview objects directly (with approval_steps)
    // so no extra fetch needed — just pass record straight to the modal.

    const handleClearFilters = () => {
        setSearchName("");
        setSearchEmpCode("");
        setOrganisationFilter("");
        setDepartmentFilter("");
        setDesignationFilter("");
        setDepartments([]);
        setDesignations([]);
    };

    // ---- Excel Export -------------------------------------------------
    // Exports exactly what's currently visible in filteredEmployees, i.e.
    // it respects the active tab AND every applied filter (org, dept,
    // designation, name search, emp code search).
    const formatStatusLabel = (status) => {
        if (status === "Confirm") return "Confirmed";
        if (status === "Extend") return "Extended";
        return status || "—";
    };

    const formatDate = (value) => {
        if (!value) return "—";
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleDateString("en-GB");
    };

    const handleExportToExcel = () => {
        if (!filteredEmployees.length) {
            toast.info("No records to export for the current filters.");
            return;
        }

        setExporting(true);
        try {
            const rows = filteredEmployees.map((record, idx) => {
                const emp = record.employee || {};
                const baseRow = {
                    "S.No": idx + 1,
                    "Employee Name": `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
                    "Employee Code": emp.emp_code || "—",
                    "Organisation": emp.organisation_name || "—",
                    "Department": emp.department_name || "—",
                    "Designation": emp.designation_name || "—",
                    "Joining Date": formatDate(emp.date_of_joining),
                    "Reporting To": emp.reporting_to_name || "—",
                };

                if (activeTab === "Probation List") {
                    baseRow["Probation End Date"] = record.final_probation_end_date
                        ? formatDate(record.final_probation_end_date)
                        : "Not Set";
                } else {
                    baseRow["Status"] = formatStatusLabel(record.overall_status);
                }

                return baseRow;
            });

            const worksheet = XLSX.utils.json_to_sheet(rows);

            // Reasonable column widths
            const colWidths = Object.keys(rows[0]).map((key) => ({
                wch: Math.max(
                    key.length,
                    ...rows.map((r) => String(r[key] ?? "").length)
                ) + 3,
            }));
            worksheet["!cols"] = colWidths;

            const workbook = XLSX.utils.book_new();
            const sheetName = activeTab.replace(/[\\/*?:[\]]/g, "").slice(0, 31);
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || "Data");

            const dateStamp = new Date().toISOString().slice(0, 10);
            const fileNameSafeTab = activeTab.replace(/\s+/g, "_");
            const fileName = `Probation_${fileNameSafeTab}_${dateStamp}.xlsx`;

            XLSX.writeFile(workbook, fileName);
            toast.success(`Exported ${rows.length} record(s) to ${fileName}`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to export data to Excel.");
        } finally {
            setExporting(false);
        }
    };
    // --------------------------------------------------------------------

    const columns = [
        {
            title: "Employee",
            key: "employee",
            fixed: "left",
            width: 200,
            render: (_, record) => {
                const emp = record.employee;
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                backgroundColor: "#0e497b",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: 14,
                                fontWeight: "bold",
                                overflow: "hidden",
                                flexShrink: 0,
                            }}
                        >
                            {emp?.photo ? (
                                <img
                                    src={emp.photo}
                                    alt=""
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                            ) : (
                                emp?.first_name?.charAt(0)
                            )}
                        </div>
                        <div>
                            <div
                                style={{ fontWeight: 600, fontSize: 13, color: "#222" }}
                            >
                                {emp?.first_name} {emp?.last_name}
                            </div>
                            <div style={{ fontSize: 11, color: "#888" }}>
                                {emp?.emp_code}
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Department",
            key: "department",
            render: (_, record) => record.employee?.department_name || "—",
        },
        {
            title: "Designation",
            key: "designation",
            render: (_, record) => record.employee?.designation_name || "—",
        },
        {
            title: "Joining Date",
            key: "joining_date",
            render: (_, record) => record.employee?.date_of_joining || "—",
        },
        {
            title: "Reporting To",
            key: "reporting_to",
            render: (_, record) => record.employee?.reporting_to_name || "—",
        },
        {
            title: activeTab === "Probation List" ? "Probation End" : "Status",
            key: "status",
            render: (_, record) => {
                if (activeTab === "Probation List") {
                    return (
                        <Tag color="blue">
                            {record.final_probation_end_date
                                ? new Date(record.final_probation_end_date).toLocaleDateString("en-GB")
                                : "Not Set"}
                        </Tag>
                    );
                }
                return (
                    <Tag color={STATUS_TAG_COLOR[record.overall_status] || "default"}>
                        {record.overall_status === "Confirm"
                            ? "Confirmed"
                            : record.overall_status === "Extend"
                                ? "Extended"
                                : record.overall_status}
                    </Tag>
                );
            },
        },

        {
            title: "Action",
            key: "action",
            fixed: "right",
            width: 120,
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => {
                        // Probation List now returns ProbationReview objects directly
                        // so record already has employee + approval_steps
                        setSelectedEmployee(record);
                        setShowModal(true);
                    }}
                    style={{
                        backgroundColor: "#0e497b",
                        borderColor: "#0e497b",
                        borderRadius: 4,
                    }}
                >
                    View Details
                </Button>
            ),
        },
    ].filter(Boolean);

    return (
        <div style={{ padding: "20px" }}>
            <ToastContainer position="top-right" autoClose={3000} />

            <center>
                <Title
                    level={5}
                    style={{ color: "#0e497b", textTransform: "uppercase" }}
                >
                    Probation Reviews
                </Title>
            </center>

            {/* Status Tab Buttons with Badge count — matches PastOnduty pattern */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "20px",
                    gap: "28px",
                    flexWrap: "wrap",
                }}
            >
                {STATUS_TABS.map((tab) => {
                    const isActive = activeTab === tab.key;
                    const count = tabCounts[tab.key] ?? 0;
                    return (
                        <Badge
                            key={tab.key}
                            count={count}
                            overflowCount={999}
                            style={{
                                backgroundColor: isActive ? tab.color : "#d9d9d9",
                                color: isActive ? "#fff" : "#595959",
                                fontWeight: 600,
                                boxShadow: "none",
                            }}
                        >
                            <Button
                                type={isActive ? "primary" : "default"}
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    backgroundColor: isActive ? tab.color : undefined,
                                    borderColor: isActive ? tab.color : undefined,
                                    minWidth: 120,
                                }}
                            >
                                {isActive && loading ? (
                                    <>
                                        <LoadingOutlined style={{ marginRight: 6 }} />
                                        {tab.label}
                                    </>
                                ) : (
                                    tab.label
                                )}
                            </Button>
                        </Badge>
                    );
                })}
                <Button
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={() => {
                        fetchData(activeTab);
                        fetchCounts();
                    }}
                >
                    Refresh
                </Button>
                <Button
                    icon={<FileExcelOutlined />}
                    loading={exporting}
                    onClick={handleExportToExcel}
                    style={{
                        backgroundColor: "#1D6F42",
                        borderColor: "#1D6F42",
                        color: "#fff",
                    }}
                >
                    Download Excel
                </Button>
            </div>

            {/* Filters — uses shared filters-container class */}
            <div className="filters-container">
                <Row gutter={[12, 12]} justify="center">
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            placeholder="Employee Name"
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            placeholder="Emp Code"
                            value={searchEmpCode}
                            onChange={(e) => setSearchEmpCode(e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Select Organisation"
                            style={{ width: "100%" }}
                            value={organisationFilter || undefined}
                            allowClear
                            onChange={(val) => {
                                setOrganisationFilter(val);
                                setDepartmentFilter("");
                                setDesignationFilter("");
                                setDepartments([]);
                                setDesignations([]);
                                if (val) {
                                    const org = Organisations.find(
                                        (o) => o.name === val
                                    );
                                    if (org) fetchDepartments(org.id);
                                }
                            }}
                        >
                            {Organisations.map((o) => (
                                <Option key={o.id} value={o.name}>
                                    {o.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            showSearch
                            placeholder="Select Department"
                            style={{ width: "100%" }}
                            value={departmentFilter || undefined}
                            allowClear
                            disabled={!organisationFilter}
                            onChange={(val) => {
                                setDepartmentFilter(val);
                                setDesignationFilter("");
                                setDesignations([]);
                                if (val) {
                                    const dept = departments.find(
                                        (d) => d.name === val
                                    );
                                    if (dept) fetchDesignations(dept.id);
                                }
                            }}
                            filterOption={(input, option) =>
                                option?.children
                                    ?.toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        >
                            {departments.map((d) => (
                                <Option key={d.id} value={d.name}>
                                    {d.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            showSearch
                            placeholder="Select Designation"
                            style={{ width: "100%" }}
                            value={designationFilter || undefined}
                            allowClear
                            disabled={!departmentFilter}
                            onChange={(val) => setDesignationFilter(val)}
                            filterOption={(input, option) =>
                                option?.children
                                    ?.toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        >
                            {designations.map((d) => (
                                <Option key={d.id} value={d.name}>
                                    {d.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Button
                            block
                            type="primary"
                            style={{
                                backgroundColor: "#0e497b",
                                borderColor: "#0e497b",
                            }}
                            onClick={handleClearFilters}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Table */}
            <div className="table-responsive">
                <Table
                    columns={columns}
                    dataSource={filteredEmployees}
                    rowKey={(r) => r.id}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: filteredEmployees.length,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "25", "50", "100"],
                        onChange: (p, s) =>
                            setPagination({ current: p, pageSize: s }),
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    bordered
                    size="middle"
                    scroll={{ x: "max-content" }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </div>

            {showModal && selectedEmployee && (
                <ProbationReviewModal
                    visible={showModal}
                    employee={selectedEmployee}
                    onClose={() => {
                        setShowModal(false);
                        fetchData(activeTab);
                        fetchCounts();
                    }}
                />
            )}
        </div>
    );
}